import React from 'react';
import { Text, View, StyleSheet } from 'react-native';

import { COLOR, FONT, FONT_SIZE, THEME, __WEB__ } from '../../constants/styles';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';

import { createEventForMultipleCases, resetState, clearEventDetails } from '../calendar/actions';

import { getCurrentEmployer } from '../../selectors/employers';
import { convertDateToTZ } from '../calendar/timezone_helper';

import { PENDING, REQUESTING, SUCCESS, FAILED } from '../../actions/statusTypes';

import { EVENT_TYPE_AIRFORCE_DEPLOYMENT } from '../../constants/eventTypes';
import { OPERATIONAL_MEDICINE, FLIGHT_MEDICINE } from '../../constants/medical_roster';

import {
  OPComponent,
  Container,
  NavBar,
  I18n,
  Utils,
  Navigation,
  DropDown,
  FormHelpers,
  DatePicker,
  Select,
  FormInput,
  TextUtils,
} from '../components';

import moment from 'moment';

import SelectedAirman from './selected_airman';

const deploymentDateRef = React.createRef();
const locationRef = React.createRef();
const deploymentEndDateRef = React.createRef();
const taskingIdInputRef = React.createRef();
const medicineTypeInputRef = React.createRef();

class DeploymentForm extends OPComponent {
  static options(passProps) {
    return {
      topBar: {
        leftButtons: [NavBar.buttons.cancel],
        rightButtons: [NavBar.buttons.save],
      },
    };
  }

  constructor(props) {
    super(props);

    this.state = {
      title: '',
      deploymentDate: props.startDate || null,
      endDate: null,
      locationItems: this.prepLocationItems(props.vaccinesByRegion),
      medicineTypeOptions: this.prepMedicineTypeItems(),
    };
  }

  navigationButtonPressed = ({ buttonId }) => {
    if (this.props.status === REQUESTING || this.props.uploadStatus === REQUESTING) return;

    if (NavBar.handleButtonPress2(buttonId, this)) return;
  };

  componentDidMount() {
    Utils.trackScreen('Airforce', 'Deployment Form');

    Navigation.registerForButtonEvents(this);
  }

  componentWillUnmount() {
    Navigation.unRegisterForButtonEvents(this);

    this.props.actions.resetState();
    this.props.actions.clearEventDetails();
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.status === SUCCESS && prevProps.status !== SUCCESS) {
      if (this._addedEvent) {
        this.props.onEventAdd && this.props.onEventAdd(this._addedEvent);
        this._addedEvent = null;
      }
      Navigation.dismissModal(this.props.componentId);
    }

    const errorMessage = this.props.errorMessage;
    if (errorMessage && errorMessage.message !== this.state.errorMessage) {
      this.setState({ errorMessage: errorMessage.message });
    }
  }

  saveDeploymentCalendarEvent = () => {
    const tz = this.props.currentEmployer.timeZone;
    const endDate = moment(this.state.deploymentDate).endOf('day').toDate();
    const startDate = moment(this.state.deploymentDate).startOf('day').toDate();

    let eventObj = {
      eventName: `Readying to: ${this.state.selectedLocation}`,
      startDate: convertDateToTZ(startDate, tz).toISOString(),
      endDate: convertDateToTZ(endDate, tz).toISOString(),
      allDay: true,
      eventTypeName: EVENT_TYPE_AIRFORCE_DEPLOYMENT,
      timeZone: tz,
    };

    const patientExtraData = {
      deployment: {
        location: this.state.selectedLocation,
        date: startDate,
        taskingId: this.state.taskingId,
        medicineType: this.state.medicineType,
      },
    };

    this._addedEvent = eventObj;

    this.props.actions.createEventForMultipleCases(
      eventObj,
      this.props.currentEmployer.employerId,
      this.props.selectedCases,
      patientExtraData
    );
  };

  validateForm = () => {
    const fieldsToValidate = [
      deploymentDateRef.current,
      deploymentEndDateRef.current,
      locationRef.current,
      taskingIdInputRef.current,
      medicineTypeInputRef.current,
    ];
    return FormHelpers.validateFields(fieldsToValidate);
  };

  onSave = () => {
    const formIsValid = this.validateForm();
    if (!formIsValid) {
      this.setState({ errorMessage: I18n.t('form.pleaseResolveErrors') });
      return;
    }

    if (!this.state.selectedLocation) {
      this.setState({
        errorMessage: I18n.t('airforce.selectDeploymentLocation'),
      });
      return;
    }

    this.saveDeploymentCalendarEvent();
  };

  handleDeploymentDate = (deploymentDate) => {
    this.setState({ deploymentDate }, this.props.onInputsChanged);
  };
  handleEndDate = (endDate) => {
    this.setState({ endDate }, this.props.onInputsChanged);
  };

  goToDeploymentDateSelect = () => {
    let selectedDate = this.state.deploymentDate ? new Date(this.state.deploymentDate) : new Date();
    if (!this.state.deploymentDate) {
      this.setState({
        deploymentDate: selectedDate,
      });
    }
    Navigation.showModal({
      screen: 'DateTimeModal',
      title: I18n.ac('airforce.deploymentDate'),
      passProps: {
        selectedDate,
        onDate: (deploymentDate) => {
          this.setState({ deploymentDate });
        },
        mode: 'date',
      },
    });
  };
  goToEndDateSelect = () => {
    let selectedDate = this.state.endDate ? new Date(this.state.endDate) : new Date();
    if (!this.state.endDate) {
      this.setState({
        endDate: selectedDate,
      });
    }
    Navigation.showModal({
      screen: 'DateTimeModal',
      title: I18n.ac('airforce.deploymentEndDate'),
      passProps: {
        selectedDate,
        onDate: (endDate) => {
          this.setState({ endDate });
        },
        mode: 'date',
      },
    });
  };

  formatDateString(date) {
    if (!date) return null;
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }
  renderDate = () => {
    if (__WEB__) {
      return (
        <DatePicker
          ref={deploymentDateRef}
          label={I18n.t('airforce.deploymentDate')}
          validateFunc={Utils.validate.date}
          required={true}
          onDate={this.handleDeploymentDate}
          selectedDate={this.state.deploymentDate}
          flexibleHeight={true}
        />
      );
    }

    return (
      <DropDown
        ref={deploymentDateRef}
        required={true}
        label={I18n.t('airforce.deploymentDate')}
        value={this.formatDateString(this.state.deploymentDate)}
        imageSource={require('../../../resources/nav-icon-calendar.png')}
        onPress={this.goToDeploymentDateSelect}
        flexibleHeight={true}
      />
    );
  };

  renderEndDate = () => {
    if (__WEB__) {
      return (
        <DatePicker
          ref={deploymentEndDateRef}
          label={I18n.t('airforce.deploymentEndDate')}
          validateFunc={Utils.validate.date}
          required={true}
          onDate={this.handleEndDate}
          selectedDate={this.state.endDate}
        />
      );
    }

    return (
      <DropDown
        ref={deploymentEndDateRef}
        required={true}
        label={I18n.t('airforce.deploymentEndDate')}
        value={this.formatDateString(this.state.endDate)}
        imageSource={require('../../../resources/nav-icon-calendar.png')}
        onPress={this.goToEndDateSelect}
      />
    );
  };

  handleSelectLocation = ({ label }) => {
    const selectedLocation = label;
    this.setState({ selectedLocation }, this.props.onInputsChanged);
  };
  prepLocationItems = (vaccinesByRegion) => {
    return Object.keys(vaccinesByRegion).map((loc) => {
      return { id: loc, label: loc };
    });
  };
  renderLocations = () => {
    const locationVal = this.state.selectedLocation;
    const val = (locationVal && { label: locationVal, id: locationVal }) || null;
    return (
      <Select
        ref={locationRef}
        required={true}
        editable={true}
        closeOnSelect={true}
        options={this.state.locationItems}
        label={I18n.t('airforce.deployingTo')}
        value={val}
        onValue={this.handleSelectLocation}
        webMode={true}
      />
    );
  };

  handleInputTaskingId = (taskingId) => {
    this.setState({ taskingId }, this.props.onInputsChanged);
  };
  renderTaskingId = () => {
    return (
      <FormInput
        ref={taskingIdInputRef}
        value={this.state.taskingId}
        required={true}
        label={I18n.t('airforce.taskingId')}
        returnKeyType="done"
        requiredErrorText="Tasking ID Required"
        onChangeText={this.handleInputTaskingId}
      />
    );
  };

  handleSelectMedicineType = ({ id }) => {
    const medicineType = id;
    this.setState({ medicineType }, this.props.onInputsChanged);
  };
  prepMedicineTypeItems = () => {
    return [
      { id: OPERATIONAL_MEDICINE, label: TextUtils.c1(OPERATIONAL_MEDICINE) },
      { id: FLIGHT_MEDICINE, label: TextUtils.c1(FLIGHT_MEDICINE) },
    ];
  };
  renderMedicineType = () => {
    const medVal = this.state.medicineType;
    const val = (medVal && { label: TextUtils.c1(medVal), id: medVal }) || null;

    return (
      <Select
        ref={medicineTypeInputRef}
        required={true}
        editable={true}
        closeOnSelect={true}
        options={this.state.medicineTypeOptions}
        label={I18n.t('airforce.medicalTeamAssignment')}
        value={val}
        onValue={this.handleSelectMedicineType}
        webMode={true}
      />
    );
  };

  renderSelectedCase = (caseId, i) => {
    if (this.props.inSplitScreen) return null;
    const c = this.props.casesById[caseId];
    const patient = c && this.props.patientsById[c.patientId];
    if (!patient) return null;

    return <SelectedAirman key={c.patientId} case={c} patient={patient} />;
  };

  render() {
    return (
      <Container
        style={THEME.container}
        renderActivityOverlay={!this.props.inSplitScreen && this.props.status === REQUESTING}
        errorMessage={this.state.errorMessage}
      >
        {this.props.selectedCases?.caseIds?.length === 1 &&
          this.props.selectedCases.caseIds.map(this.renderSelectedCase)}
        <Container
          style={[THEME.container, __WEB__ ? { paddingVertical: 10, paddingHorizontal: 60 } : null]}
          scrollView={true}
        >
          <View style={THEME.inputsContainer}>
            {this.renderDate()}
            {this.renderEndDate()}
            {this.renderLocations()}
            {this.renderMedicineType()}
            {this.renderTaskingId()}
          </View>
        </Container>
      </Container>
    );
  }
}

const styles = StyleSheet.create({
  container: {
    ...(__WEB__ ? { backgroundColor: COLOR.contrast } : THEME.container),
  },
  inSplitScreenContainer: {
    ...THEME.container,
  },
  inputContainer: {
    backgroundColor: COLOR.contrast,
    paddingHorizontal: 35,
    paddingBottom: 15,
    paddingTop: 25,
    ...(__WEB__
      ? {
          zIndex: -1,
          paddingBottom: 250,
        }
      : {}),
  },
});

function mapStateToProps(state, ownProps) {
  return {
    status: state.screens.calendar.status,
    selectedCases: state.screens.calendar.selectedCases || { caseIds: ownProps.selectedCases },
    errorMessage: state.screens.calendar.errorMessage,
    casesById: state.entities.cases,
    patientsById: state.entities.patients,
    vaccinesByRegion: state.entities.metaData.vaccinesByRegion,
    currentEmployer: getCurrentEmployer(state),
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        createEventForMultipleCases,
        resetState,
        clearEventDetails,
      },
      dispatch
    ),
  };
}

export default connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true })(
  DeploymentForm
);
